import { useCallback, useRef } from "react";
import { omnichannelInboxApi } from "../services/omnichannelInboxApi";

/**
 * Manages the compose lock + heartbeat lifecycle for the omnichannel inbox.
 *
 * Extracted from UnifiedInboxMessagingPage so the lock logic is testable
 * and reusable without coupling it to the parent's state variables.
 *
 * @param {object}   opts
 * @param {Function} opts.onLockChange  Called with `(isLocked: boolean)` when lock state changes.
 * @param {Function} [opts.onConflict]  Called with the raw error response data on a 409 conflict.
 *
 * @returns {{
 *   handleComposerFocus: (conversationId: string|number) => Promise<void>,
 *   releaseComposeLock:  (conversationId?: string|number) => void,
 *   composerLockHeldRef: React.MutableRefObject<boolean>,
 *   composerLockConversationIdRef: React.MutableRefObject<string|number|null>,
 * }}
 */
export function useComposeLock({ onLockChange, onConflict } = {}) {
  const heartbeatTimerRef           = useRef(null);
  const composerLockHeldRef         = useRef(false);
  const composerLockConversationIdRef = useRef(null);
  const composerLockPendingRef      = useRef(false);

  // ── Heartbeat: keep the lock alive while the user types ──────────────────
  const startHeartbeat = useCallback((conversationId) => {
    if (heartbeatTimerRef.current) return; // already running
    heartbeatTimerRef.current = window.setInterval(() => {
      omnichannelInboxApi.sendHeartbeat(conversationId).catch(() => {});
    }, 10_000);
  }, []);

  const stopHeartbeat = useCallback(() => {
    if (heartbeatTimerRef.current) {
      window.clearInterval(heartbeatTimerRef.current);
      heartbeatTimerRef.current = null;
    }
  }, []);

  // ── Acquire lock on first keystroke with jitter ───────────────────────────
  // A random 0-120 ms jitter staggers simultaneous keystrokes from two agents,
  // dramatically reducing the chance that both receive a 200 before either
  // heartbeat fires. The "already pending" guard prevents duplicate requests
  // from rapid re-focus events on the same conversation.
  const handleComposerFocus = useCallback(async (conversationId) => {
    if (!conversationId || composerLockHeldRef.current || composerLockPendingRef.current) return;
    composerLockPendingRef.current = true;
    try {
      const jitterMs = Math.floor(Math.random() * 120);
      await new Promise((resolve) => setTimeout(resolve, jitterMs));
      if (composerLockHeldRef.current) return; // acquired during jitter wait
      await omnichannelInboxApi.acquireConversationLock(conversationId);
      composerLockHeldRef.current = true;
      composerLockConversationIdRef.current = conversationId;
      onLockChange?.(false);
      startHeartbeat(conversationId);
    } catch (err) {
      const errCode = err?.response?.data?.error_code;
      if (err?.response?.status === 409 || errCode === "compose_locked") {
        onLockChange?.(true);
        onConflict?.(err?.response?.data);
      }
    } finally {
      composerLockPendingRef.current = false;
    }
  }, [onLockChange, onConflict, startHeartbeat]);

  // ── Release lock + stop heartbeat ────────────────────────────────────────
  const releaseComposeLock = useCallback((conversationId) => {
    stopHeartbeat();
    const lockId = conversationId ?? composerLockConversationIdRef.current;
    if (lockId && composerLockHeldRef.current) {
      omnichannelInboxApi.releaseConversationLock(lockId).catch(() => {});
    }
    composerLockHeldRef.current = false;
    composerLockPendingRef.current = false;
    composerLockConversationIdRef.current = null;
    onLockChange?.(false);
  }, [stopHeartbeat, onLockChange]);

  return {
    handleComposerFocus,
    releaseComposeLock,
    composerLockHeldRef,
    composerLockConversationIdRef,
  };
}
